import React, { useState, useEffect } from "react";
import { formatDateTime, formatAmount } from "../../utils/format";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "../../components/PrimaryButton";
import { useHistory } from "react-router-dom";
import CloseIcon from "../../icons/x.svg";
import noImage from "../../icons/noImageIcon.svg";
import EyeIcon from "../../icons/eyeB.svg";
import ImagePopupViewer from "../../components/ImagePopupViewer";

const STATUS_COLORS = {
  UNDER_APPROVAL: "#F8B916",
};
const TREATED_COLORS = {
  true: "#06D6A0",
  false: "#ADAFC6",
};

const InfoCard = ({ title, value, size = "20px" }) => (
  <div className="flex flex-col">
    <p className="text-[#707070] text-[14px] font-semibold">{title}</p>
    <p className="text-[#38485C] font-semibold" style={{ fontSize: size }}>{value}</p>
  </div>
);

const IMAGE_WIDTH_VW = 20; 

const PaymentDetailsPopup = ({ payment, onClose }) => {
  const { t } = useTranslation();
  const history = useHistory();

  const getTreatedLabel = (status) => t(`treated_status.${status}`);


  if (!payment) return null;

  const handleConsultPayment = (payment) => {
    history.push({
      pathname: `/payments/details/${payment.id}`,
    });
  };

  const statusKey = payment.status;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1050]">
      <div className="relative bg-[#F9FAFC] rounded-lg p-8 w-11/12 md:w-3/4 lg:w-2/3 shadow-lg">
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 w-8 h-8 bg-[#F2F3F3] flex items-center justify-center rounded-full shadow-[0px_20px_80px_#00000011]"
          onClick={onClose}
        >
          <img src={CloseIcon} alt="Close" />
        </button>

        {/* Header Section */}
        <div className="mb-6">
          <h1 className="text-[24px] font-semibold text-black">{t("titles.impaye_details")}</h1>
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <p className="text-[#6A78F8] text-[18px] font-semibold">
                {payment.client?.name || ""} {payment.client?.code || ""}
              </p>
              {(payment.status === "UNDER_APPROVAL") && (
                <span
                  className="text-white text-[12px] font-semibold px-2 py-1 rounded-full"
                  style={{ backgroundColor: STATUS_COLORS[statusKey] || "#A8ADC3" }}
                >
                  {t(`payment_status.${statusKey}`).toUpperCase()}
                </span>
              )}
              <span
                className="text-white text-[12px] font-semibold px-2 py-1 rounded-full"
                style={{
                  backgroundColor: TREATED_COLORS[payment.treated] || "#A8ADC3",
                }}
              >
                {getTreatedLabel(payment.treated).toUpperCase()}
              </span>
            </div>
            <PrimaryButton
              label={t(`primaryButtons.consult_payment`)}
              icon={EyeIcon}
              onClick={() => handleConsultPayment(payment)}
            />
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6 py-4 pl-[27px] bg-white rounded-lg shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
              <InfoCard title={t("rows.reference").toUpperCase()} value={payment.reference || "N/A"} />
              <InfoCard title={t("rows.paymentType")} value={payment.type?.name || "N/A"} />
              <InfoCard title={t("rows.amount")} value={formatAmount(payment.amount)} />
            </div>

            <div className="space-y-4">
              <h2 className="text-[#ACAFC6] text-[16px] font-semibold">{t("fields_types.attachedDocs")}</h2>
              {payment.deliveryNotePayments?.map((note, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-2 py-4 pl-[27px] rounded-lg bg-white shadow-[0px_6px_26px_rgba(98,107,125,0.07)]"
                >
                  <InfoCard title={t("rows.date")} value={formatDateTime(note.createdAt)} />
                  <InfoCard title={t("rows.reference").toUpperCase()} value={note.deliveryNoteNumber} />
                  <InfoCard title={t("rows.attachedAmount")} value={formatAmount(note.attachedAmount)} />
                </div>
              ))}
            </div>
          </div>

          {/* Image block with fixed viewport width and centered fallback */}
          <div className="flex-shrink-0">
            <div
              style={{ width: `${IMAGE_WIDTH_VW}vw`, aspectRatio: "4 / 3" }}
            >
              <div className="bg-white shadow rounded-[12px] p-[2px] relative">
                <div className="bg-[#F5F6FB] rounded-[12px] overflow-hidden">
                  <ImagePopupViewer src={payment.image} fallbackSrc={noImage} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDetailsPopup;
